import React, { useEffect, useState } from "react";
import Button from "../ui/Button";
import { RiAiGenerate } from "react-icons/ri";
import { CiCircleInfo } from "react-icons/ci";
import Sidebar from "../PPT/Sidebar";
import TextExtractor from "../PPT/TextExtractor";
import { generateChartDataUsingLLM, getTextFromObjects } from "../../utils/function";
import { MdOutlineInsertChart } from "react-icons/md";
import { LuBarChart3 } from "react-icons/lu";
import {
  containerName,
  PPT2IMG_BACKEND_URL,
  sasToken,
  storageAccountName,
} from "../../data/constant";
import { BlobServiceClient } from "@azure/storage-blob";
import pptxgen from "pptxgenjs";
import { LAYOUT_OPTIONS } from "../../data";
import { FaCheckCircle } from "react-icons/fa";
import { FiUpload } from "react-icons/fi";
import { TbTextResize } from "react-icons/tb";

function InsertChart({ isOpen, setIsOpen, onInsertChart, activeSlide }) {
  const [isContentVisible, setIsContentVisible] = useState(false);
  const [chartTitle, setChartTitle] = useState("");
  const [xAxisLabel, setXAxisLabel] = useState("");
  const [yAxisLabel, setYAxisLabel] = useState("");
  const [fileUploadError, setFileUploadError] = useState("");
  const [textAreaContent, setTextAreaContent] = useState("");
  const [extractedText, setExtractedText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [chartType, setChartType] = useState("bar");
  const [imageUrl, setImageUrl] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [isGeneratedByContent, setIsGeneratedByContent] = useState(false);

 

  useEffect(() => {
    const text = getTextFromObjects(activeSlide?.slideDataArray);
    console.info("Getting text from active slide");
    setTextAreaContent(!text ? "" : text);
    setImageUrl("");
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) {
      setIsContentVisible(false);
      setChartTitle("");
      setXAxisLabel("");
      setYAxisLabel("");
      setFileUploadError("");
      setTextAreaContent("");
      setIsLoading(false);
      setIsDisabled(true);
      setImageUrl("");
      setChartData(null);
      setIsGeneratedByContent(false);
    }
  }, [isOpen]);

  async function uploadFileToBlob2(fileBlob, fileName) {
    try {
      console.info("Uploading file to blob storage");
      const blobServiceClient = new BlobServiceClient(
        `https://${storageAccountName}.blob.core.windows.net/?${sasToken}`
      );
      const containerClient =
        blobServiceClient.getContainerClient(containerName);
      const blockBlobClient = containerClient.getBlockBlobClient(fileName);

      await blockBlobClient.uploadBrowserData(fileBlob, {
        blobHTTPHeaders: { blobContentType: fileBlob.type },
      });

      const fileUrl = `https://${storageAccountName}.blob.core.windows.net/${containerName}/${fileName}`;

      try {
        const response = await fetch(PPT2IMG_BACKEND_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ pptxUrl: fileUrl }),
        });

        if (!response.ok) {
          throw new Error("Failed to convert PPTX to PDF");
        }

        const data = await response.json();
        return data.uploadedUrls[0];
      } catch (error) {
        throw error;
      }
    } catch (error) {
      throw error;
    }
  }

  async function generateChartImage(chartData, chartType) {
    return new Promise(async (resolve, reject) => {
      try {
        console.info("Generating chart image");
        let pptx = new pptxgen();
        let slide = pptx.addSlide();

        slide.addChart(pptx.ChartType[chartType], chartData, {
          ...LAYOUT_OPTIONS.LAYOUT_1_MEDIA_FULL_OPTIONS_CHART,
          title: chartTitle,
          showTitle: true,
          catAxisTitle: xAxisLabel,
          valAxisTitle: yAxisLabel,
        });

        const base64String = await pptx.write("base64");
        const byteCharacters = atob(base64String);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const pptBlob = new Blob([byteArray], {
          type: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        });

        const imageUrl = await uploadFileToBlob2(pptBlob, `codemonk.pptx`);
        resolve(imageUrl);
      } catch (error) {
        reject(error);
      }
    });
  }

  async function firstStepGenerateChart(text) {
    setIsLoading(true);
    try {
      console.info("Starting chart generation process");
      const extractedData = await generateChartDataUsingLLM(text);
      if (extractedData === "null" || !extractedData) {
        setFileUploadError("Can't Generate Chart");
        return;
      }
      setChartData(extractedData);
      const url = await generateChartImage(extractedData, chartType);
      setImageUrl(url);
    } catch (error) {
      setFileUploadError("Failed to generate chart data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Sidebar
      open={isOpen}
      setOpen={setIsOpen}
      title="Insert Chart"
      isLoading={isLoading}
      isDisabled={isLoading || !imageUrl}
      rightCTA={isLoading ? "Please wait..." : "Insert"}
      onConfirm={() => {
        onInsertChart({
          extractedData: chartData,
          chartType,
          chartTitle,
          xAxisLabel,
          yAxisLabel,
          imageUrl,
          isGeneratedByContent,
        });
        setIsOpen(false);
      }}
    >
      {imageUrl ? (
        <>
          <div className="flex items-center gap-2 px-4 py-2 bg-green-200 text-black rounded-md">
            <FaCheckCircle className="text-lg bg-green-500 text-white rounded-full p-1" />
            <span>Chart generated successfully</span>
          </div>
          <div className="relative">
            <img
              src={imageUrl}
              alt="Generated Chart"
              className="w-full object-contain"
            />
          </div>

          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">
                Chart Title
              </label>
              <input
                placeholder="Chart Title"
                value={chartTitle}
                readOnly
                onChange={(e) => setChartTitle(e.target.value)}
                className="p-2 border border-gray-300 w-full rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">
                X-Axis Label
              </label>
              <input
                readOnly
                placeholder="X-Axis Label"
                value={xAxisLabel}
                onChange={(e) => setXAxisLabel(e.target.value)}
                className="p-2 border border-gray-300 w-full rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">
                Y-Axis Label
              </label>
              <input
                readOnly
                placeholder="Y-Axis Label"
                value={yAxisLabel}
                onChange={(e) => setYAxisLabel(e.target.value)}
                className="p-2 border border-gray-300 w-full rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="flex justify-between gap-4 mb-4">
            <Button
              type={!isContentVisible ? "solid" : "outline"}
              onClick={() => {
                setIsDisabled(true);
                setIsContentVisible(false);
                setFileUploadError("");
                setIsGeneratedByContent(false);
              }}
              disabled={isLoading}
              className="w-1/2"
            >
           <FiUpload className="text-xl" />   Upload File
            </Button>
            <Button
              onClick={() => {
                setIsContentVisible(true);
                setFileUploadError("");
                setIsGeneratedByContent(true);
              }}
              className="w-1/2"
              disabled={isLoading}
              type={isContentVisible ? "solid" : "outline"}
            >
              <TbTextResize className="text-xl" />
              Slide Content
            </Button>
          </div>

          {!isContentVisible ? (
            <div>
             
              <TextExtractor
                allowedFileTypes={[".xlsx", ".xls", ".csv"]}
                loading={isLoading}
                onDelete={() => {
                  setIsDisabled(true);
                  setFileUploadError("");
                }}
                onSuccess={(text) => {
                  setExtractedText(text);
                  firstStepGenerateChart(text);
                  setIsDisabled(false);
                  setFileUploadError("");
                }}
              />

              <h2 className="text-base font-bold my-4">Chart Type</h2>
              <div className="flex gap-2 mb-4">
                <div
                  onClick={() => {
                    if (!isLoading) {
                      setChartType("bar");
                      setFileUploadError("");
                    }
                  }}
                  className={`w-1/2 h-16 border flex flex-col gap-1 ${
                    isLoading
                      ? "cursor-not-allowed opacity-50"
                      : "cursor-pointer"
                  } items-center justify-center shadow-sm rounded-md ${
                    chartType === "bar" && "bg-[#e6eaff]"
                  }`}
                >
                  <LuBarChart3 className="text-2xl" />
                  <span>Bar Chart</span>
                </div>
                <div
                  onClick={() => {
                    if (!isLoading) {
                      setChartType("line");
                      setFileUploadError("");
                    }
                  }}
                  className={`w-1/2 h-16 border flex flex-col gap-1 ${
                    isLoading
                      ? "cursor-not-allowed opacity-50"
                      : "cursor-pointer"
                  } items-center justify-center shadow-sm rounded-md ${
                    chartType === "line" && "bg-[#e6eaff]"
                  }`}
                >
                  <MdOutlineInsertChart className="text-2xl" />
                  <span>Line Chart</span>
                </div>
              </div>

              {fileUploadError && (
                <div className="text-red-500 text-sm mb-2">
                  {fileUploadError}
                </div>
              )}

              <div className="p-2 mt-4 bg-gray-100 flex gap-3 items-center rounded-md text-sm">
                <CiCircleInfo className="text-3xl mt-1 text-blue-500" />A slide
                can have up to 2 media. Additional media will be added to a new
                slide.
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <h2 className="text-base font-bold">Content</h2>

              <textarea
                placeholder="Enter content here"
                value={textAreaContent}
                readOnly
                onChange={(e) => setTextAreaContent(e.target.value)}
                disabled={isLoading}
                className="w-full h-[200px] p-2 border text-sm font-serif leading-10 border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />

              <Button
                disabled={isLoading || !textAreaContent}
                onClick={() => {
                  firstStepGenerateChart(textAreaContent);
                  setFileUploadError("");
                }}
                className=""
                type="outline"
              >
                <RiAiGenerate />{" "}
                {isLoading ? "Generating Chart" : "Generate Chart"}
              </Button>

              <div className="p-2 bg-gray-100 flex gap-3 items-start rounded-md text-sm">
                <CiCircleInfo className="text-xl mt-1 text-blue-500" />
                Charts generated from slide content will remain on the same
                slide; excess media moves to the next slide.
              </div>
            </div>
          )}
        </>
      )}
    </Sidebar>
  );
}

export default InsertChart;
